import * as XLSX from 'xlsx'
import * as R from 'remeda'
import { format, isAfter, subYears } from 'date-fns'
import { standardDateFormat } from '../../../../helpers/date'
import { getFullNameOrEmail } from '../../../../redux/user'
import { CustomerTraining, TeamMember, UserTraining } from '../../../../types'
import { FormattedTraining } from '../types'
import { CustomerState } from '../../../../redux/customer'
import { ChildCustomerState } from '../../../../redux/childCustomer'

export const downloadTrainingReport = (
  customerTrainings: CustomerTraining[],
  customer: CustomerState | ChildCustomerState,
  displayInactiveMember: boolean,
  tabIndex: number,
  teamMembers: TeamMember[],
  teamMembersIndex: Record<string, TeamMember>,
  userTrainings: UserTraining[],
  yearFilterOptions: string[]
) => {
  const { city, customer_number, doctor_name, name, state, street, zip } = customer
  const wb = XLSX.utils.book_new()
  const fileName = 'Training By Practice'
  const customerData = [
    {
      A: name
    },
    {
      A: doctor_name
    },
    {
      A: street
    },
    {
      A: `${city}, ${state} ${zip}`
    },
    {
      A: `Customer #: ${customer_number}`
    }
  ]

  const ws = XLSX.utils.json_to_sheet(customerData, { skipHeader: true })

  if (teamMembers.length > 0) {
    let reportStartYear: Date
    let reportEndYear: Date = new Date()
    if (tabIndex === 0) {
      reportStartYear = subYears(reportEndYear, 1)
    } else {
      reportStartYear = new Date(`01/01/${yearFilterOptions[0]}`)
    }
    const trainingIndex = R.indexBy(
      R.filter(customerTrainings, (t) => (tabIndex === 0 ? t.is_active : !t.is_active)).map((customerTraining) => customerTraining.training),
      R.prop('course_id')
    )

    const formattedTrainings: FormattedTraining[] = userTrainings
      .filter((training) => {
        const startDate = new Date(training.start_date)
        const teamMember: TeamMember | undefined = teamMembersIndex[training.user_id]

        return (
          Boolean(trainingIndex[training.course_id]) &&
          (teamMember?.account_active || (teamMember && displayInactiveMember)) &&
          (tabIndex === 0 ? training.is_active && isAfter(startDate, reportStartYear) : true)
        )
      })
      .map((training) => {
        const teamMember: TeamMember | undefined = teamMembersIndex[training.user_id]
        const userName = getFullNameOrEmail(teamMember)
        const courseId = training?.course_id
        const credits = trainingIndex[courseId]?.ce_credits
        const year = trainingIndex[courseId]?.year
        const memberName = `${userName} ${teamMember.account_active ? '' : '(Inactive)'}`.trim()

        const formattedTraining = {
          'Team Member': memberName,
          Year: year ? `${year}` : '',
          'Training Name': trainingIndex[courseId]?.name ?? 'Unknown',
          'Due Date': training?.due_date ? standardDateFormat(training.due_date) : '',
          'Completion Date': training?.completion_date ? standardDateFormat(training.completion_date) : '',
          Status: training?.completion_date ? (tabIndex === 0 ? 'pass' : 'complete') : 'incomplete',
          'CE Credits': credits ?? 'N/A'
        }

        return formattedTraining
      })

    if (formattedTrainings.length > 0) {
      const sortedTrainings = R.sortBy(formattedTrainings, (training) => training['Team Member'])

      XLSX.utils.sheet_add_json(
        ws,
        [
          {
            A: `${format(reportStartYear, 'P')} - ${format(reportEndYear, 'P')} ${tabIndex === 0 ? '(Shows 1 full calendar year for all downloads)' : ''
              }`
          }
        ],
        { skipHeader: true, origin: 'A8' }
      )
      XLSX.utils.sheet_add_json(ws, sortedTrainings, {
        origin: 'A10'
      })
    } else {
      XLSX.utils.sheet_add_json(
        ws,
        [{ A: tabIndex === 0 ? 'There are no trainings for this practice' : 'There are no inactive trainings for this practice' }],
        { skipHeader: true, origin: -1 }
      )
    }
  } else {
    XLSX.utils.sheet_add_json(ws, [{ A: 'There are no team members in this practice' }], { skipHeader: true, origin: -1 })
  }

  XLSX.utils.book_append_sheet(wb, ws, fileName)
  XLSX.writeFile(wb, `${fileName}.xlsx`)
}

interface FormattedTrainingByYear {
  Training: string
  Year: string
  User: string
  'User Status': string
  'Assignment Date': string
  'Enrollement Date': string
  'Completion Date': string
}

export const downloadTrainingByYearReport = (
  customerTrainings: CustomerTraining[],
  customer: CustomerState | ChildCustomerState,
  teamMembers: TeamMember[],
  teamMembersIndex: Record<string, TeamMember>,
  userTrainings: UserTraining[]
) => {
  const { city, customer_number, doctor_name, name, state, street, zip } = customer
  const wb = XLSX.utils.book_new()
  const fileName = 'Training By Year'

  const customerData = [
    { A: name },
    { A: doctor_name },
    { A: street },
    { A: `${city}, ${state} ${zip}` },
    { A: `Customer #: ${customer_number}` },
  ]

  const createSheetWithCustomerHeader = () => XLSX.utils.json_to_sheet(customerData, { skipHeader: true })

  if (teamMembers.length === 0) {
    const ws = createSheetWithCustomerHeader()
    XLSX.utils.sheet_add_json(ws, [{ A: 'There are no team members in this practice' }], { skipHeader: true, origin: -1 })
    XLSX.utils.book_append_sheet(wb, ws, fileName)
    XLSX.writeFile(wb, `${fileName}.xlsx`)
    return
  }

  const trainingIndex = R.indexBy(
    customerTrainings.map((customerTraining) => customerTraining.training),
    R.prop('course_id')
  )

  const formattedTrainings: FormattedTrainingByYear[] = userTrainings
    .filter((training) => Boolean(trainingIndex[training.course_id]))
    .filter((training) => Boolean(teamMembersIndex[training.user_id]))
    .map((training) => {
      const teamMember = teamMembersIndex[training.user_id]
      const userName = getFullNameOrEmail(teamMember)

      const courseId = training.course_id
      const year = trainingIndex[courseId]?.year
      const trainingName = trainingIndex[courseId]?.name ?? 'Unknown'

      return {
        Training: trainingName,
        Year: year != null ? `${year}` : '',
        User: userName,
        'User Status': teamMember?.account_active ? 'Active' : 'Inactive',
        'Assignment Date': training.start_date ? standardDateFormat(training.start_date) : '',
        'Enrollement Date': training.enrollment_date ? standardDateFormat(training.enrollment_date) : '',
        'Completion Date': training.completion_date ? standardDateFormat(training.completion_date) : '',
      }
    })

  const trainingsByYear = R.groupBy(
    formattedTrainings.filter((training) => training.Year !== ''),
    (training) => training.Year
  )

  const years = Object.keys(trainingsByYear).sort((a, b) => Number(a) - Number(b))

  if (years.length === 0) {
    const ws = createSheetWithCustomerHeader()
    XLSX.utils.sheet_add_json(
      ws,
      [{ A: 'There are no trainings for this practice' }],
      { skipHeader: true, origin: -1 }
    )
    XLSX.utils.book_append_sheet(wb, ws, fileName)
    XLSX.writeFile(wb, `${fileName}.xlsx`)
    return
  }

  years.forEach((year) => {
    const ws = createSheetWithCustomerHeader()
    const yearTrainings = R.sortBy(trainingsByYear[year], (training) => {
      const activeSort = training['User Status'] === 'Active' ? '0' : '1'
      return `${activeSort}|${training.Training}|${training.User}`
    })

    XLSX.utils.sheet_add_json(ws, yearTrainings, { origin: 'A7' })
    XLSX.utils.book_append_sheet(wb, ws, year)
  })

  XLSX.writeFile(wb, `${fileName}.xlsx`)
}
